import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { api } from '@/api/client';
import { TYPE_LABEL_KEY, SKILL_LABEL_RU } from '@/api/ieltsMock';
import { PageHeader } from '@/components/ui/PageHeader';
import { cn } from '@/lib/utils';

/**
 * Опись базы тренажёров: что у нас есть и что из этого в ходу.
 *
 * ⚠️ ЗАЧЕМ ЭКРАН. Заказчик 07.09.2026: «чтобы видеть сколько задач всего есть в
 * базе тренажёров и сколько задач затронуто из них. Чтобы видеть — всё ли у нас
 * под контролем».
 *
 * Повод веский. До этого экрана состояние запаса добывалось запросами в базу, и
 * ровно поэтому крупный перекос жил незамеченным месяцами: из пяти видов эссе
 * существовал ОДИН, а по общему числу «шестнадцать заданий письма» выглядело
 * здоровым запасом. Цифра «всего» без разбивки по видам обманывает.
 *
 * ⚠️ ЧТО ЗНАЧИТ «В ХОДУ». Это «хоть раз кому-то досталось», а НЕ «исчерпано»:
 * одно задание достаётся многим ученикам, каждому по разу. Ноль при большом
 * запасе означает, что вид не попадает в тесты вовсе, — находка поважнее
 * нехватки, потому что нехватку видно, а мёртвый груз нет.
 */

type TypeRow = {
  type: string;
  total: number;
  used: number;
  levels: Record<string, number>;
};

type Section = {
  skill: string;
  total: number;
  used: number;
  types: TypeRow[];
};

type Inventory = {
  sections: Section[];
  generated_at: string;
};

// Уровни в порядке роста, а не как придёт с сервера: A2 левее C1 читается сразу.
const LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

/** Ниже этого числа вид считаем бедным и подсвечиваем. */
const LOW_STOCK = 10;

/**
 * Названия видов, которых нет в общем словаре типов: подтипы эссе и части речи.
 * Держим здесь, а не на сервере: сервер отдаёт коды, перевод — дело экрана.
 */
const OWN_LABEL: Record<string, string> = {
  opinion: 'Opinion',
  discussion: 'Discussion',
  advantages_disadvantages: 'Advantages / Disadvantages',
  problem_solution: 'Problem & Solution',
  two_part: 'Double Question',
  task1_academic: 'Task 1 — Academic',
  task1_general: 'Task 1 — General (letter)',
  part1: 'Part 1 — Interview',
  part2: 'Part 2 — Long Turn',
  part3: 'Part 3 — Discussion',
};

function TypeLine({ row, t }: { row: TypeRow; t: (k: string) => string }) {
  const labelKey = TYPE_LABEL_KEY[row.type];
  const name = OWN_LABEL[row.type] ?? (labelKey ? t(labelKey) : row.type);
  const low = row.total < LOW_STOCK;
  const idle = row.total > 0 && row.used === 0;

  const levels = LEVELS.filter((l) => row.levels?.[l]).map((l) => `${l} ${row.levels[l]}`);

  return (
    <div className="flex items-center gap-3 border-b border-outline py-2.5 last:border-b-0">
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-semibold text-on-surface">{name}</span>
        {/* Разбивка по уровням: «всего 60» ничего не говорит, если все 60 на C1,
            а ученики сидят на B1. */}
        {levels.length > 0 && (
          <span className="block truncate text-[11px] text-on-surface-variant">{levels.join(' · ')}</span>
        )}
      </span>

      {idle && (
        <span className="flex-none rounded-full bg-warning-container px-2 py-0.5 text-[10.5px] font-bold text-warning">
          {t('trainerPool.unused')}
        </span>
      )}

      <span className="flex-none text-right text-[12.5px] tabular-nums text-on-surface-variant">
        {row.used}
      </span>
      <span
        className={cn(
          'w-[52px] flex-none text-right text-[13.5px] font-bold tabular-nums',
          low ? 'text-error' : 'text-on-surface',
        )}
      >
        {row.total}
      </span>
    </div>
  );
}

export function AdminTrainerPoolPage() {
  const { t } = useTranslation();

  const q = useQuery({
    queryKey: ['admin', 'trainer-pool'],
    queryFn: async () => {
      const { data } = await api.get('/admin/ielts/pool/inventory');
      return data as Inventory;
    },
    // Запас меняется от генерации, а не сам по себе: обновлять чаще минуты
    // незачем, но и держать вчерашнюю правду на этом экране нельзя.
    refetchInterval: 60_000,
  });

  const d = q.data;
  const totalAll = (d?.sections ?? []).reduce((n, s) => n + s.total, 0);
  const usedAll = (d?.sections ?? []).reduce((n, s) => n + s.used, 0);

  return (
    <>
      <PageHeader title={t('trainerPool.title')} subtitle={t('trainerPool.subtitle')} />

      {q.isLoading && (
        <p className="py-6 text-center text-[13px] text-on-surface-variant">{t('common.loading')}</p>
      )}

      {q.isError && (
        <div className="rounded-[20px] bg-error-container px-[18px] py-4 text-[13px] text-on-error-container">
          {t('trainerPool.error')}
        </div>
      )}

      {d && (
        <>
          <div className="mb-3.5 rounded-[22px] bg-surface-container px-[22px] py-[18px]">
            <div className="font-display text-[22px] font-bold text-on-surface">
              {t('trainerPool.totalLine', { total: totalAll, used: usedAll })}
            </div>
            <div className="mt-1 text-[12.5px] text-on-surface-variant">
              {t('trainerPool.usedMeaning')}
            </div>
          </div>

          <div className="flex flex-col gap-3.5">
            {d.sections.map((s) => (
              <div key={s.skill} className="rounded-[20px] bg-surface-container px-[18px] py-4">
                <div className="mb-2 flex items-baseline gap-3">
                  <h3 className="flex-1 font-display text-[15px] font-bold text-on-surface">
                    {SKILL_LABEL_RU[s.skill] ?? s.skill}
                  </h3>
                  <span className="text-[12.5px] font-semibold tabular-nums text-on-surface-variant">
                    {t('trainerPool.sectionTotal', { total: s.total, used: s.used })}
                  </span>
                </div>

                {/* Шапка столбцов: без неё две цифры справа нечитаемы. */}
                <div className="flex items-center gap-3 border-b border-outline pb-2">
                  <span className="min-w-0 flex-1 text-[10.5px] uppercase tracking-[0.1em] text-on-surface-variant">
                    {t('trainerPool.colType')}
                  </span>
                  <span className="flex-none text-[10.5px] uppercase tracking-[0.1em] text-on-surface-variant">
                    {t('trainerPool.colUsed')}
                  </span>
                  <span className="w-[52px] flex-none text-right text-[10.5px] uppercase tracking-[0.1em] text-on-surface-variant">
                    {t('trainerPool.colTotal')}
                  </span>
                </div>

                {s.types.map((row) => (
                  <TypeLine key={row.type} row={row} t={t} />
                ))}
              </div>
            ))}
          </div>

          <p className="mt-3 px-1 text-[12px] leading-[1.5] text-on-surface-variant">
            {t('trainerPool.footnote', { n: LOW_STOCK })}
          </p>
        </>
      )}
    </>
  );
}
